import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  ScrollArea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import {
  AtonTargetInfo,
  BaseStationTargetInfo,
  EnvironmentalTargetInfo,
  getTargetInfoName,
  MethydTargetInfo,
  ShipInfo,
  TargetInfoType,
  TargetTypeEnum,
  VirtualAtonTargetInfo,
} from '@ais-ui/websockets';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TargetActions } from '../Map/ShipActions/TargetActions';
import { cn } from '@ais-ui/utils';
import { ShipDetailsPanel } from './ShipDetailsPanel';
import { TabsDetails } from './ShipDetailsTabs/TabsDetails';
import { AtonDetailsPanel } from './AtonDetailsPanel';
import { MethydDetailsPanel } from './MethydDetailsPanel';
import { VirtualAtonDetailsPanel } from './VirtualAtonDetailsPanel';
import { BaseStationDetailsPanel } from './BaseStationDetailsPanel';
import {
  PiArrowsClockwiseFill,
  PiCaretLeftLight,
  PiCaretRightLight,
  PiCellSignalSlash,
  PiWarning,
} from 'react-icons/pi';
import { ExtendedVesselDetailsPanel } from './ExtendedVesselDetailsPanel';
import { VesselResult } from '@ais-ui/api-spec/shipdb';
import { EnvironmentalDetailsPanel } from './EnvironmentalDetailsPanel';
import {
  DetailView,
  useViewMode,
  ViewMode,
} from '../../components/ViewModeProvider';
import { useSelectionState } from '../../components/SelectionStateProvider';
import { useSettings } from '../Settings/SettingsProvider';
import { useAisReportAge } from './useAISReportAge';
import { parseSecondsToCustomDuration } from '../utils/formatDuration';

interface TargetDetailsMapProps {
  className?: string;
  screenHeight?: boolean;
}

export type TargetTab =
  | 'base'
  | 'integrity'
  | 'register'
  | 'advancedVessel'
  | 'nextPort';

interface TargetInfoDetailsProps {
  targetInfo: TargetInfoType;
  targetType: TargetTypeEnum;
  tab: TargetTab;
}

const TargetInfoDetails = ({
  targetInfo,
  targetType,
  tab,
}: TargetInfoDetailsProps) => {
  return (
    <CardContent className="flex-1 flex flex-col overflow-hidden">
      <ScrollArea
        className="flex-grow p-0 overflow-y-auto"
        observeResize={false}
      >
        {targetType === TargetTypeEnum.BaseStation ? (
          <BaseStationDetailsPanel
            baseStationInfo={targetInfo as BaseStationTargetInfo}
          ></BaseStationDetailsPanel>
        ) : targetType === TargetTypeEnum.Aton ? (
          <AtonDetailsPanel
            atonInfo={targetInfo as AtonTargetInfo}
          ></AtonDetailsPanel>
        ) : targetType === TargetTypeEnum.VirtualAton ? (
          <VirtualAtonDetailsPanel
            virtualAtonInfo={targetInfo as VirtualAtonTargetInfo}
          ></VirtualAtonDetailsPanel>
        ) : targetType === TargetTypeEnum.Methyd ? (
          <MethydDetailsPanel
            methydInfo={targetInfo as MethydTargetInfo}
          ></MethydDetailsPanel>
        ) : targetType === TargetTypeEnum.Environmental ? (
          <EnvironmentalDetailsPanel
            environmentalInfo={targetInfo as EnvironmentalTargetInfo}
          ></EnvironmentalDetailsPanel>
        ) : targetType === TargetTypeEnum.ExtendedVessel ? (
          <ExtendedVesselDetailsPanel
            shipInfo={targetInfo as VesselResult}
            tab={tab}
          ></ExtendedVesselDetailsPanel>
        ) : (
          <ShipDetailsPanel shipInfo={targetInfo as ShipInfo} tab={tab} />
        )}
      </ScrollArea>
    </CardContent>
  );
};

interface TargetCardProps {
  className?: string;
}

export const TargetCard = ({ className }: TargetCardProps) => {
  const { viewMode, setViewMode } = useViewMode();
  const { getAisReportAge } = useAisReportAge();
  const {
    currentTargetInfo,
    currentTargetType,
    goToNextCard,
    targetInfoDetailsList,
    currentIndex,
    setTargetInfoDetailsList,
  } = useSelectionState();

  const canShowDetailedInformation = useMemo(() => {
    if (!currentTargetInfo) {
      return false;
    }

    if ('imo' in currentTargetInfo && currentTargetInfo.imo) {
      return true;
    }

    if (
      'basic' in currentTargetInfo &&
      currentTargetInfo.basic &&
      'vesselImoNumber' in currentTargetInfo.basic &&
      currentTargetInfo.basic.vesselImoNumber
    ) {
      return true;
    }

    return false;
  }, [currentTargetInfo]);

  const multipleTargetsSelected = useMemo(() => {
    return targetInfoDetailsList.length > 1;
  }, [targetInfoDetailsList]);

  const [tab, setTab] = useState<
    'base' | 'integrity' | 'register' | 'advancedVessel' | 'nextPort'
  >('base');

  const nextTargetName = useMemo(() => {
    if (!multipleTargetsSelected) {
      return '';
    }
    if (currentIndex < targetInfoDetailsList.length - 1) {
      const nextTargetInfo = targetInfoDetailsList[currentIndex + 1];
      return getTargetInfoName(
        nextTargetInfo.extractedTargetInfo[0],
        nextTargetInfo.extractedTargetInfoType
      );
    } else {
      const nextTargetInfo = targetInfoDetailsList[0];
      return getTargetInfoName(
        nextTargetInfo.extractedTargetInfo[0],
        nextTargetInfo.extractedTargetInfoType
      );
    }
  }, [currentIndex, multipleTargetsSelected, targetInfoDetailsList]);

  const name = useMemo(() => {
    if (!currentTargetInfo || !currentTargetType) {
      return '';
    }

    return getTargetInfoName(currentTargetInfo, currentTargetType);
  }, [currentTargetInfo, currentTargetType]);

  const aisReportAgeResult = useMemo(() => {
    if (!currentTargetInfo || !currentTargetType) {
      return null;
    }

    return getAisReportAge(currentTargetInfo, currentTargetType);
  }, [getAisReportAge, currentTargetInfo, currentTargetType]);

  if (!currentTargetInfo || !currentTargetType) {
    return null;
  }

  const onExit = () => {
    if (viewMode === ViewMode.Detail) {
      setViewMode(ViewMode.Normal);
    }

    setTargetInfoDetailsList([]);
  };

  return (
    <Card
      id="targetinfo-card"
      className={cn(
        'relative flex flex-col min-w-[464px] max-w-[464px] z-10 shadow-lg',
        className
      )}
      style={{ maxHeight: 'calc(100vh - 2rem)' }}
    >
      {canShowDetailedInformation &&
        (viewMode === ViewMode.Detail ? (
          <Button
            onClick={() => setViewMode(ViewMode.Normal)}
            className="absolute top-14 -left-3 bg-background rounded-full border border-text-gray-900 text-gray-700 hover:bg-secondary h-6 w-6 p-0"
          >
            <PiCaretRightLight className="h-4 w-4" />
          </Button>
        ) : (
          <Button
            onClick={() => {
              setViewMode(ViewMode.Detail, DetailView.Vessel);
            }}
            className="absolute top-14 -left-3 bg-background rounded-full border border-text-gray-900 text-gray-700 hover:bg-secondary h-6 w-6 p-0"
          >
            <PiCaretLeftLight className="h-4 w-4" />
          </Button>
        ))}
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center">
          <CardTitle className="flex text-lg font-medium text-muted-semibold space-x-4">
            <div>{name}</div>
            {aisReportAgeResult && aisReportAgeResult.isAisReportOld && (
              <Tooltip>
                <TooltipTrigger>
                  <PiCellSignalSlash className="h-6 w-6 text-yellow-600"></PiCellSignalSlash>
                </TooltipTrigger>
                <TooltipContent>
                  Last AIS message is{' '}
                  {parseSecondsToCustomDuration(
                    aisReportAgeResult.aisReportAge
                  )}{' '}
                  old
                </TooltipContent>
              </Tooltip>
            )}
          </CardTitle>
          <div className="flex space-x-1 items-center">
            {multipleTargetsSelected && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={'ghost'}
                    size={'icon'}
                    onClick={goToNextCard}
                  >
                    <PiArrowsClockwiseFill className="h-5 w-5"></PiArrowsClockwiseFill>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {`Go to next target: ${nextTargetName}`}
                </TooltipContent>
              </Tooltip>
            )}
            <Button onClick={onExit} variant="ghost" size="icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
                className="h-4 w-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                ></path>
              </svg>
            </Button>
          </div>
        </div>
      </CardHeader>
      {currentTargetType === TargetTypeEnum.Ship ||
      currentTargetType === TargetTypeEnum.ExtendedVessel ? (
        <TabsDetails
          tab={tab}
          setTab={setTab}
          imo={(currentTargetInfo as any)?.imo}
        />
      ) : null}
      <TargetInfoDetails
        targetInfo={currentTargetInfo}
        targetType={currentTargetType}
        tab={tab}
      />
      <CardFooter className="min-h-52">
        <TargetActions
          targetInfo={currentTargetInfo}
          targetType={currentTargetType}
        ></TargetActions>
      </CardFooter>
    </Card>
  );
};

interface TargetCardStackProps {
  className?: string;
}

const TargetCardStack = ({ className }: TargetCardStackProps) => {
  const { targetInfoDetailsList, currentTargetInfo, currentTargetType } =
    useSelectionState();

  if (!currentTargetInfo || !currentTargetType) {
    return null;
  }

  return (
    <div
      className={cn(
        'relative max-h-screen',
        className,
        targetInfoDetailsList.length === 2 && 'ml-3',
        targetInfoDetailsList.length >= 2 && 'ml-6'
      )}
    >
      <TargetCard className="h-screen"></TargetCard>
      <div className="absolute top-0 left-0">
        {targetInfoDetailsList.length >= 3 && (
          <Card
            id="stack-card-2"
            className={cn(
              'absolute z-1 min-w-[464px] max-w-[464px] h-screen -top-3 -left-6 shadow-md cursor-default pointer-events-auto'
            )}
            style={{ maxHeight: 'calc(100vh - 2rem)' }}
          ></Card>
        )}
        {targetInfoDetailsList.length >= 2 && (
          <Card
            id="stack-card-1"
            className={cn(
              'absolute z-2 min-w-[464px] max-w-[464px] h-screen -top-1.5 -left-3 shadow-md cursor-default pointer-events-auto'
            )}
            style={{ maxHeight: 'calc(100vh - 2rem)' }}
          ></Card>
        )}
      </div>
    </div>
  );
};

export const TargetDetails = React.memo(
  ({ className }: TargetDetailsMapProps) => {
    const { targetInfoDetailsList, setTargetInfoDetailsList } =
      useSelectionState();

    const handleEscape = useCallback(
      (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setTargetInfoDetailsList([]);
        }
      },
      [setTargetInfoDetailsList]
    );

    useEffect(() => {
      window.addEventListener('keydown', handleEscape);

      return () => {
        window.removeEventListener('keydown', handleEscape);
      };
    }, [handleEscape]);

    if (targetInfoDetailsList.length === 0) {
      return null;
    }

    return <TargetCardStack className={className} />;
  }
);

TargetDetails.displayName = 'TargetDetails';
